/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
/**
 * Tab Animation Type Definitions
 *
 * These types support parsing ASCII guitar tablature and animating it on the fretboard.
 */

/**
 * Represents a single note position on the fretboard
 */
export interface TabPosition {
  /** String number (0 = high E, 5 = low E for standard 6-string) */
  string: number;

  /** Fret number (0 = open string, -1 = muted/not played) */
  fret: number;

  /** Relative timing offset in beats (calculated from tab spacing) */
  timing: number;

  /** Duration in beats (how long to hold the note) */
  duration: number;

  /** Optional: technique indicators (h=hammer-on, p=pull-off, b=bend, etc.) */
  technique?: 'hammer' | 'pull' | 'bend' | 'slide' | 'vibrato' | 'mute';

  /** Target fret for slides, bends, hammer-ons, pull-offs (e.g., 5s7 means slide from 5 to 7) */
  targetFret?: number;

  /** Original column position in the tab (for debugging) */
  column?: number;
}

/**
 * A complete sequence of tab positions ready for animation
 */
export interface TabSequence {
  /** Array of positions in temporal order */
  positions: TabPosition[];

  /** Base tempo in BPM (beats per minute) */
  tempo: number;

  /** Time signature (e.g., "4/4", "3/4") */
  timeSignature?: string;

  /** Optional title/description */
  title?: string;

  /** Total duration in beats */
  totalBeats: number;

  /** Number of strings in the tab (for validation) */
  stringCount: number;
}

/**
 * Current state of animation playback
 */
export interface AnimationState {
  /** Is the animation currently playing? */
  isPlaying: boolean;

  /** Current position index in the sequence */
  currentIndex: number;

  /** Current time in beats */
  currentBeat: number;

  /** Playback speed multiplier (1.0 = normal, 0.5 = half speed, 2.0 = double speed) */
  speedMultiplier: number;

  /** Whether to loop when reaching the end */
  loop: boolean;
}

/**
 * Result of parsing a tab notation string
 */
export interface TabParseResult {
  /** Successfully parsed sequence, or null if parsing failed */
  sequence: TabSequence | null;

  /** Any errors encountered during parsing */
  errors: string[];

  /** Warnings (non-fatal issues like unusual spacing) */
  warnings: string[];

  /** Original tab text that was parsed */
  originalTab: string;

  /** Detected string tuning (if parseable from tab) */
  detectedTuning?: string[];
}

/**
 * Configuration options for the tab parser
 */
export interface TabParserConfig {
  /** Default tempo if not specified in tab (in BPM) */
  defaultTempo: number;

  /** How many dashes equal one beat (affects timing granularity) */
  dashesPerBeat: number;

  /** Whether to infer timing from spacing */
  useSpacingForTiming: boolean;

  /** Minimum spacing between notes (in dashes) */
  minSpacing: number;

  /** Whether to parse technique indicators (h, p, b, etc.) */
  parseTechniques: boolean;
}

/**
 * Default parser configuration
 */
export const DEFAULT_TAB_PARSER_CONFIG: TabParserConfig = {
  defaultTempo: 120,
  dashesPerBeat: 4, // 4 dashes = 1 beat, so each dash = 1/4 beat (16th note at 4/4)
  useSpacingForTiming: true,
  minSpacing: 1,
  parseTechniques: true
};

/**
 * Animation control commands
 */
export type AnimationCommand =
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'STOP' }
  | { type: 'SEEK'; position: number }
  | { type: 'SET_SPEED'; speed: number }
  | { type: 'TOGGLE_LOOP' };
